import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { buildAliasHead } from "@/lib/alias-routes";
import { buildCalculatorLd } from "@/lib/page-schemas";
import { GoldSipCalculator } from "@/components/GoldSipCalculator";
import { CalculatorFaqAccordion, RelatedCalculators } from "@/components/calculator/CalculatorEducation";

const TITLE = "Gold Investment Calculator — Gold SIP & Past Returns (INR / PKR) | DrHint";
const DESCRIPTION =
  "Free gold investment calculator: plan a monthly gold SIP with year-by-year growth, or check what gold bought 1, 5 or 10 years ago is worth today at the live rate.";
const SLUG = "gold-investment-calculator";

const ROICalculator = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.ROICalculator })),
);

const FAQS = [
  { q: "How is a gold SIP return calculated?", a: "Each monthly instalment buys gold that grows at the assumed annual return, compounded monthly. The future value uses FV = P × [((1+i)^n − 1) ÷ i] × (1+i), where P is the monthly amount, i the monthly rate and n the number of months." },
  { q: "What annual return should I assume for gold?", a: "In rupee terms gold has averaged around 9–11% a year over long periods, helped by currency depreciation. Use 8% for a cautious plan and 12% for an optimistic one — and remember single years can be negative." },
  { q: "Is digital gold or a gold ETF better for SIP?", a: "Gold ETFs and gold mutual funds are regulated and have low costs. Digital gold is easy to start but carries a buy–sell spread of around 3% plus GST in India. Physical jewellery is the costliest because making charges are not recovered on sale." },
  { q: "Does the calculator include tax?", a: "No. Results are before tax. In India, gains on gold ETFs and funds are taxed under capital-gains rules; in Pakistan, gains on physical gold are generally not taxed at the point of sale for individuals. Check current rules before you sell." },
];

export const Route = createFileRoute("/gold-investment-calculator")({
  head: () => {
    const base = buildAliasHead({ title: TITLE, description: DESCRIPTION, path: `/${SLUG}` });
    const url = `https://drhint.com/${SLUG}`;
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        ...buildCalculatorLd({
          url,
          name: "Gold Investment & SIP Calculator",
          description: DESCRIPTION,
          applicationCategory: "FinanceApplication",
          featureList: [
            "Monthly gold SIP projection in INR or PKR",
            "Year-by-year invested vs value chart",
            "Grams of gold equivalent at today's live rate",
            "Back-test a past lump-sum gold purchase",
          ],
          howTo: {
            name: "How to calculate gold SIP returns",
            totalTime: "PT2M",
            steps: [
              { name: "Choose currency and monthly amount", text: "Select INR or PKR and enter how much you will invest in gold every month." },
              { name: "Set period and expected return", text: "Enter the number of years and an assumed annual gold return, such as 10%." },
              { name: "Read the projection", text: "See total invested, estimated gain, final value, gram equivalent and the year-by-year chart." },
            ],
          },
          faqs: FAQS,
          trail: [
            { name: "Home", item: "https://drhint.com/" },
            { name: "Calculators", item: "https://drhint.com/calculators" },
            { name: "Gold Investment Calculator", item: url },
          ],
        }),
      ],
    };
  },
  loader: ({ context }) => { void context.queryClient.prefetchQuery(ratesQueryOptions); },
  component: Page,
});

function Page() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pb-2 pt-10">
        <nav aria-label="Breadcrumb" className="mb-3 text-xs text-muted-foreground">
          <Link to="/calculators" className="hover:text-primary">Calculators</Link> / Gold Investment Calculator
        </nav>
        <h1 className="text-3xl font-black tracking-tight md:text-4xl">Gold Investment Calculator (SIP & Lump Sum)</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          Two questions people ask before buying gold: "If I put aside a fixed amount every month, what will I have?" and
          "What would gold I bought years ago be worth now?" The first calculator answers the SIP question; the second
          back-tests a past purchase against today's live price.
        </p>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-6">
        <GoldSipCalculator />
      </section>
      <section className="mx-auto max-w-6xl px-4 py-6">
        <h2 className="mb-4 text-2xl font-bold">Lump-sum back-test (PKR)</h2>
        <Suspense fallback={<div className="h-80 rounded-3xl border border-border" />}>
          <ROICalculator />
        </Suspense>
      </section>
      <article className="mx-auto max-w-4xl space-y-8 px-4 pb-12">
        <section>
          <h2 className="text-xl font-bold">Example: ₹5,000 a month for 10 years</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You invest ₹6,00,000 in total. At 10% a year the SIP grows to about ₹10.3 lakh; at 8% about ₹9.2 lakh; at
            12% about ₹11.6 lakh. The gap between those three numbers is the real lesson — the assumed return matters more
            than a few hundred rupees of monthly amount.
          </p>
        </section>
        <CalculatorFaqAccordion faqs={FAQS} />
        <RelatedCalculators
          items={[
            { path: "/today-gold-rate-calculator-india", name: "Gold Rate Calculator India", description: "Price with making and GST", icon: "🥇" },
            { path: "/today-gold-rate-calculator-pakistan", name: "Gold Rate Calculator Pakistan", description: "Per tola with making", icon: "🥇" },
            { path: "/tools/compound-interest-visualizer", name: "Compound Interest Calculator", description: "Compare with FD or savings", icon: "📈" },
            { path: "/tools/inflation-purchasing-power-calculator", name: "Inflation Calculator", description: "Real value of money over time", icon: "💸" },
            { path: "/gold-purity-calculator", name: "Gold Purity Calculator", description: "Karat to pure gold", icon: "💎" },
            { path: "/zakat-calculator", name: "Zakat Calculator", description: "2.5% on gold and savings", icon: "🕌" },
          ]}
        />
      </article>
    </Layout>
  );
}
